import { DownloadOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import MetricDateRangeFilter from '@/common/components/MetricDateRangeFilter';
import MetricProjectionWarningPopover from '@/common/components/MetricProjectionWarningPopover';
import OperationAnalysisFilterLayout, {
  operationAnalysisPageContainerStylish,
} from '@/common/components/OperationAnalysisFilterLayout';
import OperationEntityScopeSelect from '@/common/components/OperationEntityScopeSelect';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { queryMetricEffectivePeriod } from '@/common/services/metric-effective-period';
import { queryOperationAnalysisSubjects } from '@/common/services/operation-analysis-scope';
import {
  OPERATION_ANALYSIS_METADATA_STALE_TIME,
  getMetricDateRangePresets,
  getMetricEffectivePeriodQueryKey,
  getMetricEffectiveStartDate,
} from '@/common/utils/metric-effective-period';
import {
  buildOperationEntityTree,
  getOperationAnalysisSubjectsQueryKey,
  getOperationAnalysisScopeQueryPatch,
  hasOperationAnalysisStores,
  resolveOperationAnalysisScopeQuery,
} from '@/common/utils/operation-analysis-scope';
import { isDebugQueryEnabled } from '@/common/utils/query';
import StoreProfitReport from '../components/StoreProfitReport';
import { queryStoreProfitReport } from '../services/profit-report';
import {
  getFactReportDateParams,
  getFactReportAccessibleSections,
  getFactReportExportCsv,
  getFactReportExportFilename,
  getFactReportParams,
  getFactReportQueryPatch,
  getFactReportSectionControls,
} from '../utils/profit-report';

const DATE_FORMAT = 'YYYY-MM-DD';
const LEGACY_SCOPE_KEYS = ['departmentLevel2', 'expenseDepartment', 'storeId', 'subjectId'];

const Store360 = () => {
  const { session } = useAppContext();
  const { organizationId } = useOrganizationPath();
  const [query, setQuery] = useQueryParams();
  const debug = isDebugQueryEnabled(query);
  const [sectionControls, setSectionControls] = useState({});
  const identityKey =
    session.status === 'authenticated' && session.user?.id && session.role
      ? `${session.user.id}:${session.role}`
      : null;
  const {
    data: subjectResponse = {},
    isError: subjectsError,
    isPending: subjectsPending,
    isSuccess: subjectsLoaded,
  } = useQuery({
    enabled: Boolean(organizationId && identityKey),
    queryFn: () => queryOperationAnalysisSubjects({ organizationId }),
    queryKey: getOperationAnalysisSubjectsQueryKey(organizationId, identityKey),
    staleTime: OPERATION_ANALYSIS_METADATA_STALE_TIME,
  });
  const {
    data: periodResponse = {},
    isError: periodError,
    isPending: periodPending,
    isSuccess: periodLoaded,
  } = useQuery({
    enabled: Boolean(organizationId && identityKey),
    queryFn: () => queryMetricEffectivePeriod({ organizationId }),
    queryKey: getMetricEffectivePeriodQueryKey(organizationId),
    staleTime: OPERATION_ANALYSIS_METADATA_STALE_TIME,
  });
  const effectiveStartAt = periodResponse.data?.effectiveStartAt ?? null;
  const effectiveStartDate = getMetricEffectiveStartDate(effectiveStartAt);
  const subjects = useMemo(() => subjectResponse.data || [], [subjectResponse.data]);
  const subjectTree = useMemo(() => buildOperationEntityTree(subjects), [subjects]);
  const { queryPatch: scopeQueryPatch, scope } = useMemo(
    () => resolveOperationAnalysisScopeQuery(query, subjectTree),
    [query, subjectTree],
  );
  const hasStores = hasOperationAnalysisStores(scope, subjectTree);
  const dateQueryPatch = useMemo(
    () => getFactReportQueryPatch(query, undefined, effectiveStartAt),
    [effectiveStartAt, query],
  );
  const reportDateParams = useMemo(
    () => getFactReportDateParams(query, undefined, effectiveStartAt),
    [effectiveStartAt, query],
  );
  const reportParams = useMemo(
    () => getFactReportParams({ reportDateParams, scope }),
    [reportDateParams, scope],
  );
  const hasLegacyScope = LEGACY_SCOPE_KEYS.some((key) => query[key] !== undefined);

  useEffect(() => {
    if (subjectsLoaded && (Object.keys(scopeQueryPatch).length || hasLegacyScope)) {
      setQuery(scopeQueryPatch, {
        remove: LEGACY_SCOPE_KEYS,
        replace: true,
      });
    }
  }, [hasLegacyScope, scopeQueryPatch, setQuery, subjectsLoaded]);

  useEffect(() => {
    if (
      !periodPending &&
      !periodError &&
      subjectsLoaded &&
      !Object.keys(scopeQueryPatch).length &&
      !hasLegacyScope &&
      Object.keys(dateQueryPatch).length
    ) {
      setQuery(dateQueryPatch, { replace: true });
    }
  }, [
    dateQueryPatch,
    hasLegacyScope,
    periodError,
    periodPending,
    scopeQueryPatch,
    setQuery,
    subjectsLoaded,
  ]);

  const {
    data: reportResponse = {},
    isError: reportError,
    isFetching: reportFetching,
  } = useQuery({
    enabled: Boolean(organizationId && identityKey && periodLoaded && subjectsLoaded && hasStores),
    queryFn: () => queryStoreProfitReport({ organizationId, ...reportParams }),
    queryKey: ['store-profit-report', organizationId, identityKey, reportParams],
  });
  const sections = useMemo(
    () => getFactReportAccessibleSections(reportResponse.sections || [], session.role),
    [reportResponse.sections, session.role],
  );
  const reportData = useMemo(() => ({ ...reportResponse, sections }), [reportResponse, sections]);

  useEffect(() => {
    setSectionControls((current) => getFactReportSectionControls(sections, current));
  }, [sections]);

  const exportProfitReport = () => {
    const csv = getFactReportExportCsv(sections, { sectionControls });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getFactReportExportFilename(reportDateParams);
    link.click();
    URL.revokeObjectURL(url);
  };
  const reportLoading = reportFetching || periodPending;
  let errorMessage;
  if (periodError) {
    errorMessage = '指标有效周期加载失败，请刷新重试';
  } else if (reportError) {
    errorMessage = '损益报表加载失败，请刷新重试';
  }

  let content = subjectsLoaded ? (
    <Empty description="当前范围暂无可分析门店，请重新选择经营范围" />
  ) : null;
  if (hasStores) {
    content = (
      <StoreProfitReport
        key={`${organizationId}:${identityKey}`}
        data={reportData}
        debug={debug}
        errorMessage={errorMessage}
        identityKey={identityKey}
        loading={reportLoading}
        organizationId={organizationId}
        reportParams={reportParams}
        role={session.role}
        sectionControls={sectionControls}
        onSectionControlsChange={setSectionControls}
      />
    );
  }
  if (subjectsError) {
    content = <Alert showIcon title="经营主体加载失败，请刷新重试" type="error" />;
  }

  return (
    <PageContainer
      loading={subjectsPending || periodPending}
      extra={
        <OperationAnalysisFilterLayout
          actions={
            <Space.Compact>
              {debug ? <MetricProjectionWarningPopover warnings={reportResponse.warnings} /> : null}
              <Tooltip title="导出">
                <Button
                  aria-label="导出"
                  disabled={reportLoading || !hasStores || !sections.length}
                  icon={<DownloadOutlined />}
                  type="text"
                  onClick={exportProfitReport}
                />
              </Tooltip>
            </Space.Compact>
          }
          dateFilter={
            <MetricDateRangeFilter
              ariaLabel="报表周期"
              minDate={effectiveStartDate || undefined}
              placement="bottomRight"
              presets={getMetricDateRangePresets(effectiveStartDate)}
              value={[dayjs(reportDateParams.startDate), dayjs(reportDateParams.endDate)]}
              onChange={(range) =>
                setQuery({
                  endDate: range?.[1]?.format(DATE_FORMAT),
                  startDate: range?.[0]?.format(DATE_FORMAT),
                })
              }
            />
          }
          scopeFilter={
            <OperationEntityScopeSelect
              treeData={subjectTree}
              value={scope}
              onChange={(nextScope) => setQuery(getOperationAnalysisScopeQueryPatch(nextScope))}
            />
          }
        />
      }
      stylish={operationAnalysisPageContainerStylish}
    >
      {content}
    </PageContainer>
  );
};

export default Store360;
